import type { Layer } from "@common/constants";
import { Vec, type Vector } from "@common/utils/vector";
import {
    type CombatSound,
    type GunTraits,
    IMPACT_SOUNDS,
    type ImpactKind,
    nearMissSound,
    ricochetChance,
    SoundThrottle,
    TakePicker
} from "./combatSoundsLogic";
import { type SoundOptions, SoundManager } from "./soundManager";

/**
 * The combat sounds as the game plays them (the rules that pick them: combatSoundsLogic.ts; the sounds: made by
 * tools/sfx/combat_sounds.py). Each plays one of its takes, never the same twice running, a little differently each
 * time (pitch and loudness), dulled by distance; a flood of them (a shotgun's pellets into one body, full auto into
 * one wall) plays once.
 *
 * Before the combat sounds are in the sound sheet (a browser holding an older one), each call says so (false) and the
 * caller plays the sound it always did.
 */

/** how a gunshot plays: a hair different each shot (no machine-gun sameness), dulled far off, echoing round */
export const GUNSHOT: Partial<SoundOptions> = { variation: { pitch: 0.025, volume: 0.06 }, air: true, room: 0.16 };
/** a gun's cycling (a bolt worked, a pump racked): close-up mechanics, a touch of the room */
export const GUN_CYCLE: Partial<SoundOptions> = { variation: { pitch: 0.02, volume: 0.05 }, air: true, room: 0.06 };
/** an explosion: dulled far off, a big echo */
export const EXPLOSION: Partial<SoundOptions> = { variation: { pitch: 0.04, volume: 0.05 }, air: true, room: 0.45 };

class CombatSoundsClass {
    private readonly _takes = new TakePicker();
    /** hits on players: one sound per body per 45 ms (nine pellets are one thud) */
    private readonly _bodies = new SoundThrottle(8, 100);
    /** bullets into things: one per obstacle per 35 ms */
    private readonly _impacts = new SoundThrottle(10, 100);
    private readonly _ricochets = new SoundThrottle(2, 200);
    private readonly _misses = new SoundThrottle(4, 100);
    private readonly _confirms = new SoundThrottle(1, 40);

    /** the combat sounds are in the sound sheet */
    get ready(): boolean {
        return SoundManager.has("flesh_hit_1");
    }

    /** out of earshot: nothing played, and the floods' limits not used up by it (0.85: far fights seen through a scope did) */
    private _far(position: Vector, maxRange: number): boolean {
        return Vec.len(Vec.sub(SoundManager.position, position)) >= maxRange;
    }

    private _play(sound: CombatSound, options: Partial<SoundOptions>): void {
        SoundManager.play(this._takes.pick(sound), options);
    }

    /**
     * a bullet hit a player: a body's thud, or the ring of their helmet or the "tunk" of their vest with the thud
     * under it; a straw training dummy only thuds. false: the caller plays the old hit sound.
     */
    bodyHit(id: number, position: Vector, layer: Layer, armor: "helmet" | "vest" | undefined, straw: boolean): boolean {
        if (!this.ready) return false;
        if (this._far(position, 110) || !this._bodies.allow(`p${id}`, performance.now(), 45)) return true;
        const common: Partial<SoundOptions> = { position, falloff: 0.25, maxRange: 110, layer, air: true, room: 0.06 };
        if (straw) {
            this._play("impact_dirt", { ...common, speed: 1.25, volume: 0.9, variation: { pitch: 0.06, volume: 0.1 } });
        } else if (armor) {
            this._play(armor === "helmet" ? "helmet_hit" : "vest_hit", { ...common, variation: { pitch: 0.05, volume: 0.1 } });
            this._play("flesh_hit", { ...common, volume: 0.4, variation: { pitch: 0.06, volume: 0.1 } });
        } else {
            this._play("flesh_hit", { ...common, variation: { pitch: 0.06, volume: 0.12 } });
        }
        return true;
    }

    /**
     * a bullet struck an obstacle or a wall of this kind: its impact (and now and then a ricochet off a hard one);
     * `glanced`: it reflected off (steel that turns bullets), so it always whines away. false: the caller plays the
     * material's old hit sound (no sound of ours for it, or none loaded yet).
     */
    impact(kind: ImpactKind, id: number, position: Vector, layer: Layer, glanced: boolean): boolean {
        const sound = IMPACT_SOUNDS[kind];
        if (!sound || !this.ready) return false;
        const now = performance.now();
        if (this._far(position, 110) || !this._impacts.allow(`o${id}`, now, 35)) return true;
        this._play(sound.sound, {
            position,
            falloff: 0.25,
            maxRange: 110,
            layer,
            speed: sound.speed,
            volume: sound.volume,
            variation: { pitch: 0.06, volume: 0.12 },
            air: true,
            room: 0.1
        });
        // (steel that turns every bullet would whine on every shot: about half of them)
        if (Math.random() < (glanced ? 0.45 : ricochetChance(kind))) this.ricochet(position, layer, glanced ? 0.9 : 0.6);
        return true;
    }

    /** a bullet glancing off and whining away. false: the caller plays the old reflection sound */
    ricochet(position: Vector, layer: Layer, volume = 0.9): boolean {
        if (!this.ready) return false;
        if (this._far(position, 128) || !this._ricochets.allow("r", performance.now(), 0)) return true;
        this._play("ricochet", { position, falloff: 0.3, maxRange: 128, layer, volume, variation: { pitch: 0.08, volume: 0.1 }, air: true, room: 0.12 });
        return true;
    }

    /** a bullet passed close by: a rifle's cracks, anything slower whooshes. false: the caller plays the old whiz */
    nearMiss(traits: GunTraits | undefined, position: Vector, layer: Layer): boolean {
        if (!this.ready) return false;
        if (this._far(position, 64) || !this._misses.allow("m", performance.now(), 0)) return true;
        const sound = nearMissSound(traits);
        this._play(sound, { position, layer, falloff: 1, maxRange: 64, variation: { pitch: sound === "bullet_crack" ? 0.04 : 0.08, volume: 0.1 } });
        return true;
    }

    /** your hit landed (on armour, or the one that finished them). false: the caller plays its own tick */
    confirm(kind: "hit" | "armor" | "kill"): boolean {
        if (!this.ready) return false;
        // (a kill always sounds; hits at most one every 40 ms: a burst's or a shotgun's hits run together)
        if (kind !== "kill" && !this._confirms.allow("c", performance.now(), 0)) return true;
        const sound: CombatSound = kind === "kill" ? "kill_confirm" : kind === "armor" ? "hit_confirm_armor" : "hit_confirm";
        this._play(sound, { variation: { pitch: kind === "kill" ? 0.015 : 0.03, volume: 0.06 } });
        return true;
    }
}

export const CombatSounds = new CombatSoundsClass();
